// "Filter assets by location" (DS-3776): configuration block shown in the body
// of an asset question once the filter is enabled from Advanced options. It
// mirrors the "Update data" (MeterSetting) block: a bordered, rounded container
// with a title and a prominent dropdown that lists the form's location
// questions. Reads the questions from FormBuilderContext (same pattern as
// MeterConfigItem) so it does not need the list threaded down as a prop.
import { useContext, useMemo } from 'react';

import { AddRounded } from '@mui/icons-material';
import { Box, Button, FormControl, MenuItem, Select, SxProps, Theme } from '@mui/material';

import { useLanguage } from '../../hooks';
import { FormBuilderContext } from './FormBuilderProvider';

const customStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    border: '1px solid #DDE0E5',
    borderRadius: '16px',
    padding: '16px',
  },
  title: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
  },
  select: {
    height: '34px',
    fontSize: '12px',
  },
  menuItem: { fontSize: '12px' },
  menuPaper: { maxHeight: '350px' },
  placeholder: {
    color: '#B8BABF',
  },
  createQuestionButton: {
    fontSize: '12px',
    lineHeight: '20px',
    alignSelf: 'flex-start',
    height: '24px',
    '&:hover': { backgroundColor: 'transparent' },
    '& svg': {
      width: '14px',
      height: '14px',
    },
  },
} satisfies Record<string, SxProps<Theme>>;

const isLocationQuestion = (question: { id: string; type: string; nestableLocations?: unknown }) =>
  question.type === 'select_metadata' && question.nestableLocations != null;

export interface LocationFilterSettingProps {
  /** Id of the asset question that owns this filter. */
  questionId: string;
  /**
   * Set when the asset question lives inside a repeatable group. The dropdown then
   * offers the group's own location questions (each row filters by its own) *and*
   * the form's top-level ones (one location filters every row). Absent means a
   * top-level asset question, which may only link to a top-level question: it has
   * no row to read an answer from.
   */
  tableId?: string;
  /** Id of the currently linked location question, if any. */
  value?: string;
  onFilterByChange: (
    id: string,
    filterBy: { questionId: string; questionType: string } | null,
  ) => void;
  /**
   * Creates a Locations question right after the asset question and links it.
   * Without this the dropdown would be a dead end whenever the form has no
   * location question yet, which is the state every new form starts in.
   */
  onCreateLocationQuestion: (questionId: string) => void;
}

export function LocationFilterSetting({
  questionId,
  tableId,
  value,
  onFilterByChange,
  onCreateLocationQuestion,
}: LocationFilterSettingProps) {
  const lang = useLanguage();
  const { questions } = useContext(FormBuilderContext);

  // Siblings of the same group first, then the form's own location questions. Both
  // are valid links and they mean different things at answering time, so the label
  // says which is which: two questions can easily share a name.
  const availableQuestions = useMemo(() => {
    const topLevel = questions
      .filter((question) => question.id !== questionId && isLocationQuestion(question))
      .map((question) => ({ question, scope: 'form' as const }));

    if (!tableId) return topLevel;

    const siblings = (questions.find((question) => question.id === tableId)?.questions ?? [])
      .filter((question) => question.id !== questionId && isLocationQuestion(question))
      .map((question) => ({ question, scope: 'table' as const }));

    return [...siblings, ...topLevel];
  }, [questions, questionId, tableId]);

  const optionLabel = (option: (typeof availableQuestions)[number]) =>
    option.scope === 'table'
      ? `${option.question.name} · ${lang.form_builder.filterAssetsByLocationSameRow}`
      : `${option.question.name} · ${lang.form_builder.filterAssetsByLocationWholeForm}`;

  // A missing/stale link is an incomplete configuration, not an error: keep the
  // dropdown in a neutral (placeholder) state instead of showing a red value.
  const isValidValue =
    !!value && availableQuestions.some((option) => option.question.id === value);

  const handleChange = (locationQuestionId: string) => {
    const selected = availableQuestions.find(
      (option) => option.question.id === locationQuestionId,
    );
    if (!selected) return;
    onFilterByChange(questionId, {
      questionId: selected.question.id,
      questionType: selected.question.type,
    });
  };

  return (
    <Box sx={customStyles.container}>
      <Box sx={customStyles.title}>{lang.form_builder.filterAssetsByLocationTitle}</Box>
      <FormControl fullWidth>
        <Select
          displayEmpty
          value={isValidValue ? value : ''}
          sx={customStyles.select}
          onChange={(event) => handleChange(event.target.value as string)}
          MenuProps={{ sx: customStyles.menuPaper }}
          data-testid={`filter-assets-by-location-select-${questionId}`}
          renderValue={(selected) => {
            const selectedOption = availableQuestions.find(
              (option) => option.question.id === selected,
            );
            if (!selectedOption) {
              return (
                <Box component='span' sx={customStyles.placeholder}>
                  {lang.form_builder.filterAssetsByLocationPlaceholder}
                </Box>
              );
            }
            return tableId ? optionLabel(selectedOption) : selectedOption.question.name;
          }}
        >
          {availableQuestions.map((option) => (
            <MenuItem
              key={option.question.id}
              sx={customStyles.menuItem}
              value={option.question.id}
            >
              {tableId ? optionLabel(option) : option.question.name}
            </MenuItem>
          ))}
          {/* Always last, so a form with no location question yet still offers a way
              forward instead of an empty list. */}
          <MenuItem
            onClick={() => onCreateLocationQuestion(questionId)}
            data-testid={`filter-assets-by-location-create-${questionId}`}
          >
            <Button
              variant='text'
              color='primary'
              startIcon={<AddRounded />}
              sx={customStyles.createQuestionButton}
            >
              {lang.form_builder.filterAssetsByLocationCreateQuestion}
            </Button>
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}
